import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { formatCurrency, formatListingPrice, formatNumber } from "@/lib/portalHelpers";

export const PremiumComparisonModal = ({ listings, onClose, onRequestViewing, open }) => {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const syncSwipeState = useMemo(
    () => () => {
      const container = scrollerRef.current;
      if (!container) return;

      const nextScrollable = container.scrollWidth > container.clientWidth + 8;
      setIsScrollable(nextScrollable);
      if (!nextScrollable) {
        setActiveIndex(0);
        return;
      }

      const cards = Array.from(container.querySelectorAll("[data-premium-comparison-card='true']"));
      const centerPoint = container.scrollLeft + container.clientWidth / 2;
      let nextIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.clientWidth / 2;
        const distance = Math.abs(cardCenter - centerPoint);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          nextIndex = index;
        }
      });

      setActiveIndex(nextIndex);
    },
    [],
  );

  useEffect(() => {
    if (!open) return undefined;
    const container = scrollerRef.current;
    if (!container) return undefined;

    container.scrollTo({ left: 0, behavior: "auto" });
    syncSwipeState();
    container.addEventListener("scroll", syncSwipeState, { passive: true });
    window.addEventListener("resize", syncSwipeState);

    return () => {
      container.removeEventListener("scroll", syncSwipeState);
      window.removeEventListener("resize", syncSwipeState);
    };
  }, [listings, open, syncSwipeState]);

  const progressPercent = isScrollable && listings.length ? ((activeIndex + 1) / listings.length) * 100 : 100;

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#001935]/55 px-4 py-6 backdrop-blur-sm" data-testid="signature-advisor-premium-comparison-modal">
      <div className="relative flex h-[min(90vh,920px)] w-[min(90vw,1380px)] max-w-[1380px] origin-center flex-col overflow-hidden rounded-[32px] border border-[#D4AF37]/35 bg-[#FDFBF7] shadow-[0_28px_90px_rgba(0,25,53,0.3)] animate-[premiumModalIn_280ms_cubic-bezier(0.22,1,0.36,1)]">
        <div className="h-1.5 w-full bg-[#001935]/8" data-testid="signature-advisor-premium-comparison-progress-track">
          <div
            className="h-full rounded-r-full bg-[linear-gradient(90deg,#D4AF37_0%,#f3d98c_100%)] transition-[width] duration-300"
            data-testid="signature-advisor-premium-comparison-progress-bar"
            style={{ width: `${Math.max(14, progressPercent)}%` }}
          />
        </div>
        <div className="flex items-start justify-between gap-4 border-b border-[#D4AF37]/20 px-6 py-5 md:px-8" data-testid="signature-advisor-premium-comparison-header">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Signature Comparison</p>
            <h2 className="mt-2 text-2xl font-semibold text-[#001935] md:text-4xl">Premium Comparison</h2>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-[#5f5a4e] md:text-base">A curated side-by-side view of the strongest comparable opportunities in the unified inventory.</p>
          </div>
          <button className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/35 bg-white text-[#8b6d16] transition hover:bg-[#fff4db]" data-testid="signature-advisor-premium-comparison-close-button" onClick={onClose} type="button">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-8" data-testid="signature-advisor-premium-comparison-content">
          <style>
            {`@keyframes premiumModalIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}
          </style>
          <div className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 xl:grid-cols-3" ref={scrollerRef}>
            {listings.map((listing) => (
              <div className="flex h-full min-w-[84vw] snap-center flex-col overflow-hidden rounded-[28px] border border-[#D4AF37]/28 bg-white shadow-[0_18px_50px_rgba(0,25,53,0.08)] md:min-w-0" data-premium-comparison-card="true" data-testid={`signature-advisor-premium-comparison-card-${listing.id}`} key={listing.id}>
                <div className="aspect-[1.55/1] overflow-hidden bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.18),transparent_40%),linear-gradient(180deg,#fffaf1_0%,#f3ead7_100%)]">
                  {listing.image_url ? <img alt={listing.title} className="h-full w-full object-cover" data-testid={`signature-advisor-premium-comparison-image-${listing.id}`} decoding="async" loading="lazy" src={listing.image_url} /> : null}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full border border-[#D4AF37]/30 bg-[#fff4db] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`signature-advisor-premium-comparison-usp-${listing.id}`}>
                      {listing.signature_usp || "Signature Match"}
                    </span>
                    {listing.contextual_badge ? (
                      <span className="rounded-full border border-[#001935]/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#42566f]" data-testid={`signature-advisor-premium-comparison-badge-${listing.id}`}>
                        {listing.contextual_badge}
                      </span>
                    ) : null}
                  </div>
                  <h3 className="mt-4 text-2xl font-bold text-[#001935] md:text-[2rem]" data-testid={`signature-advisor-premium-comparison-title-${listing.id}`}>{listing.title}</h3>
                  <div className="mt-4 grid gap-3 text-sm leading-7 text-[#4b4b4b] md:text-base">
                    <p data-testid={`signature-advisor-premium-comparison-district-${listing.id}`}><strong>District:</strong> {listing.district}</p>
                    <p data-testid={`signature-advisor-premium-comparison-tenure-${listing.id}`}><strong>Tenure:</strong> {listing.tenure || "Ask advisor"}</p>
                    <p data-testid={`signature-advisor-premium-comparison-price-${listing.id}`}><strong>Entry:</strong> {formatListingPrice(listing)}</p>
                    <p className="font-bold text-[#8b6d16]" data-testid={`signature-advisor-premium-comparison-psf-${listing.id}`}><strong>PSF:</strong> {formatCurrency(listing.psf || 0, 0)}</p>
                    <p data-testid={`signature-advisor-premium-comparison-size-${listing.id}`}><strong>Size:</strong> {formatNumber(listing.sqft || 0)} sqft</p>
                  </div>
                  {listing.neighbor_reason ? <p className="mt-4 rounded-[18px] bg-[#fff7e5] px-4 py-3 text-sm leading-7 text-[#5f5a4e]" data-testid={`signature-advisor-premium-comparison-reason-${listing.id}`}>{listing.neighbor_reason}</p> : null}
                  <div className="mt-auto pt-5">
                    <button className="w-full rounded-full bg-[#D4AF37] px-4 py-3 text-sm font-bold text-[#1A1A1A] transition hover:bg-[#c79f29]" data-testid={`signature-advisor-premium-comparison-request-viewing-${listing.id}`} onClick={() => onRequestViewing(listing)} type="button">
                      📅 Request Viewing
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {listings.length > 1 ? (
            <div className="mt-5 flex items-center justify-center gap-2 md:hidden" data-testid="signature-advisor-premium-comparison-pagination-dots">
              {listings.map((listing, index) => (
                <button
                  aria-label={`Go to comparison card ${index + 1}`}
                  className={`h-2.5 rounded-full transition-all duration-300 ${activeIndex === index ? "w-8 bg-[#D4AF37] shadow-[0_0_16px_rgba(212,175,55,0.65)]" : "w-2.5 bg-[#D4AF37]/35"}`}
                  data-testid={`signature-advisor-premium-comparison-dot-${index + 1}`}
                  key={listing.id}
                  onClick={() => {
                    const container = scrollerRef.current;
                    const card = container?.querySelectorAll("[data-premium-comparison-card='true']")?.[index];
                    if (container && card) {
                      container.scrollTo({ left: card.offsetLeft - 4, behavior: "smooth" });
                    }
                  }}
                  type="button"
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};